import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Container,
  Paper,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Formik, FormikHelpers } from "formik";
import { object, string, ref } from "yup";
import { useSnackbar } from "notistack";
import instance from "../../../constants/config";

interface LocationState {
  email: string;
  otpVerified: boolean;
}

interface FormValues {
  newPassword: string;
  confirmPassword: string;
}

const defaultValues: FormValues = {
  newPassword: "",
  confirmPassword: "",
};

const formValidation = object({
  newPassword: string()
    .min(6, "Password must be at least 8 characters long")
    .required("New password is required"),
  confirmPassword: string()
    .oneOf([ref("newPassword")], "Passwords must match")
    .required("Please confirm your password"),
});

const CreateNewPassword: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const { email, otpVerified } = (location.state as LocationState) || {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  // Redirect if no email or OTP not verified
  useEffect(() => {
    if (!email || !otpVerified) {
      enqueueSnackbar("Access denied. Please verify your OTP first.", {
        variant: "error",
      });
      navigate("/login"); // or wherever you want to redirect
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, otpVerified, navigate]);

  const handleSubmit = async (
    values: FormValues,
    formik: FormikHelpers<FormValues>
  ): Promise<void> => {
    setIsLoading(true);

    try {
      const response = await instance.post("/Auth/reset_passsword", {
        email,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });

      enqueueSnackbar(response.data.message || "Password reset successfully!", {
        variant: "success",
      });

      // Reset form
      formik.resetForm();

      // Redirect to login page
      navigate("/login", {
        state: {
          message:
            "Password reset successful! Please login with your new password.",
        },
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to reset password. Please try again.";

      enqueueSnackbar(errorMessage, {
        variant: "error",
      });

      // Handle specific field errors
      if (error.response?.data?.field) {
        formik.setFieldError(error.response.data.field, errorMessage);
      }
    } finally {
      setIsLoading(false);
      formik.setSubmitting(false);
    }
  };

  const togglePasswordVisibility = (): void => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = (): void => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleGoBack = (): void => {
    navigate(-1); // Go back to previous screen
  };

  if (!email || !otpVerified) {
    return null; // Will redirect in useEffect
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box textAlign="center" mb={3}>
          <Typography variant="h4" component="h1" gutterBottom>
            Create New Password
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={2}>
            Enter a new password for your account
          </Typography>
          <Typography variant="body1" fontWeight="bold" color="primary">
            {email}
          </Typography>
        </Box>

        <Formik
          initialValues={defaultValues}
          validationSchema={formValidation}
          onSubmit={handleSubmit}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit} noValidate>
              <Box mb={3}>
                <TextField
                  fullWidth
                  name="newPassword"
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  value={formik.values.newPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(
                    formik.touched.newPassword && formik.errors.newPassword
                  )}
                  helperText={
                    formik.touched.newPassword && formik.errors.newPassword
                  }
                  disabled={isLoading}
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  name="confirmPassword"
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(
                    formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                  )}
                  helperText={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  }
                  disabled={isLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleConfirmPasswordVisibility}
                          edge="end"
                          aria-label="toggle confirm password visibility"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box mb={3}>
                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={isLoading || !formik.isValid}
                  sx={{ mb: 2, height: 48 }}
                  startIcon={
                    isLoading && <CircularProgress size={20} color="inherit" />
                  }
                >
                  {isLoading ? "Updating Password..." : "Update Password"}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleGoBack}
                  disabled={isLoading}
                  sx={{ height: 48 }}
                >
                  Go Back
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Paper>
    </Container>
  );
};

export default CreateNewPassword;
